import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  CheckIcon,
  ArrowPathIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  EnvelopeIcon,
} from '@heroicons/react/20/solid'
import { DocumentTextIcon, BuildingOfficeIcon, UserIcon, CalendarIcon } from '@heroicons/react/24/outline'
import { quotesApi, type QuoteWithRelations } from '../../lib/api'
import { LineItemEditor } from '../../components/quotes/LineItemEditor'

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
  pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  approved: { bg: 'bg-blue-100', text: 'text-blue-800' },
  sent: { bg: 'bg-purple-100', text: 'text-purple-800' },
  accepted: { bg: 'bg-green-100', text: 'text-green-800' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800' },
  expired: { bg: 'bg-orange-100', text: 'text-orange-800' },
}

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isConverting, setIsConverting] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const { data: quote, isLoading, error } = useQuery({
    queryKey: ['quotes', id],
    queryFn: () => quotesApi.get(id!),
    enabled: !!id,
  })

  const submitMutation = useMutation({
    mutationFn: () => quotesApi.submit(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', id] })
      queryClient.invalidateQueries({ queryKey: ['quotes', 'stats'] })
    },
  })

  const convertMutation = useMutation({
    mutationFn: () => quotesApi.convert(id!),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      navigate(`/orders/${data.order.id}`)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => quotesApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      navigate('/quotes')
    },
  })

  const approveMutation = useMutation({
    mutationFn: () => quotesApi.approve(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', id] })
      queryClient.invalidateQueries({ queryKey: ['quotes', 'stats'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (reason?: string) => quotesApi.reject(id!, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', id] })
      queryClient.invalidateQueries({ queryKey: ['quotes', 'stats'] })
      setShowRejectModal(false)
      setRejectReason('')
    },
  })

  const sendMutation = useMutation({
    mutationFn: () => quotesApi.send(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', id] })
      queryClient.invalidateQueries({ queryKey: ['quotes', 'stats'] })
    },
  })

  const acceptMutation = useMutation({
    mutationFn: () => quotesApi.accept(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', id] })
      queryClient.invalidateQueries({ queryKey: ['quotes', 'stats'] })
    },
  })

  if (isLoading) return <div className="p-8">Loading...</div>
  if (error || !quote) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900">Quote not found</h2>
          <Link to="/quotes" className="text-brand-600 hover:text-brand-700 mt-2 inline-block">
            Back to Quotes
          </Link>
        </div>
      </div>
    )
  }

  const formatPrice = (price: string | null, currency: string = 'EUR') => {
    if (!price) return '-'
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(parseFloat(price))
  }

  const formatDate = (date: string | Date | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const isExpired = quote.validUntil && new Date(quote.validUntil) < new Date()

  // Check for proxy products (items without a productId but with proxyProductId)
  const proxyItems = (quote.lineItems || []).filter(item => item.proxyProductId && !item.productId)
  const hasUnresolvedProxies = proxyItems.length > 0

  const handleSubmit = () => {
    if (confirm('Submit this quote for approval?')) {
      submitMutation.mutate()
    }
  }

  const handleConvert = () => {
    if (confirm('Convert this quote to an order? This action cannot be undone.')) {
      setIsConverting(true)
      convertMutation.mutate()
    }
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this quote?')) {
      deleteMutation.mutate()
    }
  }

  const handleApprove = () => {
    if (confirm('Approve this quote?')) {
      approveMutation.mutate()
    }
  }

  const handleReject = () => {
    setShowRejectModal(true)
  }

  const handleConfirmReject = () => {
    rejectMutation.mutate(rejectReason || undefined)
  }

  const handleSend = () => {
    if (confirm('Mark this quote as sent to the customer?')) {
      sendMutation.mutate()
    }
  }

  const handleAccept = () => {
    if (confirm('Mark this quote as accepted by the customer?')) {
      acceptMutation.mutate()
    }
  }

  return (
    <>
      {/* Secondary column - Quote info */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <Link
              to="/quotes"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Quotes
            </Link>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-brand-100 flex items-center justify-center">
                <DocumentTextIcon className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{quote.quoteNumber}</h2>
                <p className="text-sm text-gray-500">
                  {quote.account?.name || quote.cardName || 'No account'}
                </p>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex px-3 py-1.5 text-sm font-medium rounded-full ${STATUS_COLORS[quote.status]?.bg || 'bg-gray-100'} ${STATUS_COLORS[quote.status]?.text || 'text-gray-700'}`}>
              {quote.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
            {isExpired && quote.status !== 'accepted' && (
              <span className="inline-flex px-3 py-1.5 text-sm font-medium rounded-full bg-red-100 text-red-800">
                Expired
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-2">
            {/* Draft: Submit, Delete */}
            {quote.status === 'draft' && (
              <>
                <button
                  onClick={handleSubmit}
                  disabled={submitMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-brand-500 hover:bg-brand-600 disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  Submit for Approval
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-red-600 shadow-sm border border-red-300 hover:bg-red-50 disabled:opacity-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete Quote
                </button>
              </>
            )}

            {/* Pending Approval: Approve, Reject */}
            {quote.status === 'pending_approval' && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <HandThumbUpIcon className="h-4 w-4" />
                  Approve
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-red-600 shadow-sm border border-red-300 hover:bg-red-50 disabled:opacity-50"
                >
                  <HandThumbDownIcon className="h-4 w-4" />
                  Reject
                </button>
              </>
            )}

            {/* Approved: Send to Customer, Convert */}
            {quote.status === 'approved' && !quote.convertedToOrderId && (
              <>
                <button
                  onClick={handleSend}
                  disabled={sendMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                >
                  <EnvelopeIcon className="h-4 w-4" />
                  Send to Customer
                </button>
                <button
                  onClick={handleConvert}
                  disabled={convertMutation.isPending || isConverting}
                  className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${isConverting ? 'animate-spin' : ''}`} />
                  Convert to Order
                </button>
              </>
            )}

            {/* Sent: Mark Accepted */}
            {quote.status === 'sent' && (
              <button
                onClick={handleAccept}
                disabled={acceptMutation.isPending}
                className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" />
                Mark Accepted
              </button>
            )}

            {/* Accepted: Convert to Order */}
            {quote.status === 'accepted' && !quote.convertedToOrderId && (
              <button
                onClick={handleConvert}
                disabled={convertMutation.isPending || isConverting}
                className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 ${isConverting ? 'animate-spin' : ''}`} />
                Convert to Order
              </button>
            )}
          </div>

          {/* Quick Info */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Quote Details</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Total</dt>
                <dd className="font-medium text-gray-900">
                  {formatPrice(quote.docTotal, quote.docCurrency)}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Items</dt>
                <dd className="font-medium text-gray-900">{quote._count?.lineItems || quote.lineItems?.length || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Currency</dt>
                <dd className="font-medium text-gray-900">{quote.docCurrency}</dd>
              </div>
              {quote.discountPercent && parseFloat(quote.discountPercent) > 0 && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Discount</dt>
                  <dd className="font-medium text-gray-900">{quote.discountPercent}%</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Dates */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Dates</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Created</dt>
                <dd className="font-medium text-gray-900">{formatDate(quote.docDate)}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Valid Until</dt>
                <dd className={`font-medium ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatDate(quote.validUntil)}
                </dd>
              </div>
              {quote.approvedAt && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Approved</dt>
                  <dd className="font-medium text-gray-900">{formatDate(quote.approvedAt)}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Converted Order Link */}
          {quote.convertedToOrderId && (
            <div className="bg-green-50 p-4 rounded-lg ring-1 ring-green-200">
              <h3 className="text-sm font-medium text-green-800 mb-2">Converted to Order</h3>
              <Link
                to={`/orders/${quote.convertedToOrderId}`}
                className="text-sm text-green-700 hover:text-green-900 underline"
              >
                View Order
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
          {/* Mobile back link */}
          <div className="xl:hidden mb-6">
            <Link
              to="/quotes"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Quotes
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Quote {quote.quoteNumber}</h1>
            <p className="mt-1 text-gray-500">Created on {formatDate(quote.docDate)}</p>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Account */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Account</h3>
              </div>
              {quote.account ? (
                <Link
                  to={`/accounts/${quote.accountId}`}
                  className="text-brand-600 hover:text-brand-700 font-medium"
                >
                  {quote.account.name}
                </Link>
              ) : (
                <p className="text-gray-500">{quote.cardName || 'No account selected'}</p>
              )}
              {quote.cardCode && (
                <p className="text-sm text-gray-500 mt-1">Customer #: {quote.cardCode}</p>
              )}
            </div>

            {/* Contact */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Contact</h3>
              </div>
              {quote.contact ? (
                <Link
                  to={`/contacts/${quote.contactId}`}
                  className="text-brand-600 hover:text-brand-700 font-medium"
                >
                  {quote.contact.firstName} {quote.contact.lastName}
                </Link>
              ) : (
                <p className="text-gray-500">No contact selected</p>
              )}
            </div>
          </div>

          {/* Proxy Products Warning */}
          {hasUnresolvedProxies && (
            <div className="mb-6 rounded-md bg-amber-50 border border-amber-200 p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0" />
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-amber-800">
                    {proxyItems.length} Unresolved Proxy Product{proxyItems.length > 1 ? 's' : ''}
                  </h3>
                  <p className="mt-1 text-sm text-amber-700">
                    This quote has products that could not be matched to the catalog.
                    Resolve them before converting to an order.
                  </p>
                  <div className="mt-3">
                    <Link
                      to="/cpq/proxy-products"
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-md"
                    >
                      <QuestionMarkCircleIcon className="h-4 w-4 mr-1" />
                      Resolve Proxy Products
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Line Items */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h2>
            {quote.status === 'draft' ? (
              // Editable line items for draft quotes
              <LineItemEditor
                quoteId={id!}
                lineItems={quote.lineItems || []}
                docCurrency={quote.docCurrency}
                isEditable={true}
                onItemsChange={() => queryClient.invalidateQueries({ queryKey: ['quotes', id] })}
              />
            ) : quote.lineItems && quote.lineItems.length > 0 ? (
              // Read-only line items table for non-draft quotes
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        List Price
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Offer Price
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quote.lineItems.map((item) => {
                      const isProxy = item.proxyProductId && !item.productId
                      return (
                      <tr key={item.id} className={isProxy ? 'bg-amber-50' : ''}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {item.lineNum + 1}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            {item.itemCode || '-'}
                            {isProxy && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                <QuestionMarkCircleIcon className="h-3 w-3 mr-0.5" />
                                Proxy
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {item.itemDescription}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {item.quantity} {item.uomCode || ''}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                          {formatPrice(item.listPrice, quote.docCurrency)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatPrice(item.offerPrice, quote.docCurrency)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                          {item.discountPercent}%
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {formatPrice(item.lineTotal, quote.docCurrency)}
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={7} className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                        Subtotal
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                        {formatPrice(
                          quote.lineItems.reduce((sum, item) => sum + parseFloat(item.lineTotal || '0'), 0).toString(),
                          quote.docCurrency
                        )}
                      </td>
                    </tr>
                    {quote.vatSum && parseFloat(quote.vatSum) > 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 text-right text-sm text-gray-500">
                          VAT
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-500">
                          {formatPrice(quote.vatSum, quote.docCurrency)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={7} className="px-4 py-3 text-right text-base font-bold text-gray-900">
                        Total
                      </td>
                      <td className="px-4 py-3 text-right text-base font-bold text-gray-900">
                        {formatPrice(quote.docTotal, quote.docCurrency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-500">No line items</p>
              </div>
            )}
          </div>

          {/* Approval Info */}
          {quote.approvedById && (
            <div className="mb-8 bg-blue-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Approval Information</h3>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-blue-600">Approved By</dt>
                  <dd className="font-medium text-blue-900">
                    {quote.approvedBy
                      ? `${quote.approvedBy.firstName || ''} ${quote.approvedBy.lastName || ''}`
                      : 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-blue-600">Approved On</dt>
                  <dd className="font-medium text-blue-900">{formatDate(quote.approvedAt)}</dd>
                </div>
              </dl>
              {quote.approvalReason && (
                <p className="mt-2 text-sm text-blue-700">Reason: {quote.approvalReason}</p>
              )}
            </div>
          )}

          {/* Opportunity Link */}
          {quote.opportunity && (
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Linked Opportunity</h3>
              <Link
                to={`/opportunities/${quote.opportunityId}`}
                className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700"
              >
                <CalendarIcon className="h-4 w-4" />
                {quote.opportunity.name}
              </Link>
            </div>
          )}

          {/* Timestamps */}
          <div className="border-t pt-6 mt-8">
            <dl className="flex gap-8 text-xs text-gray-500">
              <div>
                <dt className="inline">Created: </dt>
                <dd className="inline">{formatDate(quote.createdAt)}</dd>
              </div>
              <div>
                <dt className="inline">Updated: </dt>
                <dd className="inline">{formatDate(quote.updatedAt)}</dd>
              </div>
              {quote.owner && (
                <div>
                  <dt className="inline">Owner: </dt>
                  <dd className="inline">{quote.owner.firstName} {quote.owner.lastName}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRejectModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                  onClick={() => setShowRejectModal(false)}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <HandThumbDownIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    Reject Quote
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-3">
                      Please provide a reason for rejecting this quote. This will be visible to the quote owner.
                    </p>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={3}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                      placeholder="Reason for rejection (optional)"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  disabled={rejectMutation.isPending}
                  onClick={handleConfirmReject}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto disabled:opacity-50"
                >
                  {rejectMutation.isPending ? 'Rejecting...' : 'Reject Quote'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
